/**
 * geometryUtils — geometric helpers for DOM manipulation.
 *
 */

import { getPathsBoundaryPoints } from './svgUtils.js'

/**
 * Converts an azimuth (CAD convention) to a CSS `rotate()` angle in
 * degrees that points the plan/north arrow icon north-up in the browser.
 *
 * **Coordinate system difference**:
 * - CAD / edifice.json: 0° = east (+x axis), angle increases counter-clockwise,
 *   so due north is stored as 90°.
 * - Browser CSS rotate: 0° = "up" (north), angle increases clockwise.
 *
 * The result is clamped to the range (−180, 180] to keep rotations minimal.
 *
 * @param {number} azimuth — from `building.azimuth` or `apartment.azimuth` in the project JSON
 * @returns {number} rotation angle in degrees, in range (−180, 180]
 */
export function getRotateToNorthAngleDeg(azimuth) {
  const limited = azimuth % 360
  const angle = -1 * (90 - limited)
  // `|| 0` normalizes −0 (produced when azimuth is exactly 90, 450, …) to +0.
  return (angle > 180 ? angle - 360 : angle) || 0
}

// ── Angle helpers ──────────────────────────────────────────────────────────────────

/**
 * Smallest positive angular distance between two angles (degrees),
 * accounting for wrap-around. Result is always in [0, 180].
 *
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
function _shortestAngularDistance(a, b) {
  return Math.abs(((a - b + 180 + 360) % 360) - 180)
}

/**
 * Returns the index of the angle in `anglesArray` closest to `targetAngle`
 * by shortest angular distance (wrap-around aware).
 *
 * Tie-breaking: when `preferLast` is `true` (default) the last index wins
 * (`≤`); when `false` the first index wins (`<`).
 *
 * @param {number}   targetAngle
 * @param {number[]} anglesArray
 * @param {boolean}  [preferLast=true]
 * @returns {number} index into `anglesArray`; -1 when the array is empty
 */
export function getClosestAngleIndex(targetAngle, anglesArray, preferLast = true) {
  if (anglesArray.length === 0) return -1
  let closestIndex = 0
  let minDiff = Infinity
  for (let i = 0; i < anglesArray.length; i++) {
    const diff = _shortestAngularDistance(targetAngle, anglesArray[i])
    if (preferLast ? diff <= minDiff : diff < minDiff) {
      minDiff = diff
      closestIndex = i
    }
  }
  return closestIndex
}

// ── SVG compartment geometry ────────────────────────────────────────────────────

/**
 * Computes the dominant direction angle of a compartment, defined as the
 * angle from the plan’s viewBox centre to the area centroid of the
 * compartment’s boundary polygon.
 *
 * Uses SVG coordinate conventions: 0° = east (+x), increasing clockwise
 * (Y-down), range [0, 360).
 *
 * Returns `null` when the compartment path cannot be found in the SVG, its
 * `d` attribute is missing, or the path yields no parseable vertices.
 *
 * **Requires a mounted SVG element** — queries the live DOM.
 *
 * @param {SVGSVGElement} svgElement        — the mounted plan SVG
 * @param {number}        compartmentNumber — value of the path `number` attribute
 * @returns {number|null}
 */
export function getCompartmentDominantVectorAngle(svgElement, compartmentNumber) {
  if (!svgElement || compartmentNumber == null) return null

  const path = svgElement.querySelector(`path.compartment[number="${compartmentNumber}"]`)
  if (!path) return null

  const points = getPathsBoundaryPoints(path.getAttribute('d') ?? '')
  if (points.length === 0) return null

  // Centre from the SVG viewBox attribute ("minX minY width height")
  const vb = (svgElement.getAttribute('viewBox') ?? '').split(/\s+/).map(Number)
  const cx = (vb[0] ?? 0) + (vb[2] ?? 0) / 2
  const cy = (vb[1] ?? 0) + (vb[3] ?? 0) / 2

  let sumX = 0
  let sumY = 0
  for (const [x, y] of points) {
    sumX += x - cx
    sumY += y - cy
  }

  return ((Math.atan2(sumY, sumX) * 180) / Math.PI + 360) % 360
}
